<template>

  <!-- Overlay Start -->
  <div class="overlay"></div>
  <!-- Overlay End -->

  <!-- Register & Login Start -->
  <div class="section login-section-padding">
    <div class="container">

      <!-- Register & Login Wrapper Start -->
      <div class="register-login-wrapper">
        <div class="row align-items-center">
          <div class="col-lg-6">

            <!-- Register & Login Images Start -->
            <div class="register-login-images margin-top-0">
              <div class="shape-1">
                <img src="/assets/images/shape/shape-26.png" alt="Shape">
              </div>


              <div class="images">
                <img src="/assets/images/register-login.png" alt="Register Login">
              </div>
            </div>
            <!-- Register & Login Images End -->

          </div>
          <div class="col-lg-6">

            <!-- Register & Login Form Start -->
            <div class="register-login-form login-box">
              <h3 class="title">Login <span>Now</span></h3>

              <div class="form-wrapper">
                <form @submit.prevent="login">
                  <!-- Single Form Start -->
                  <div class="single-form">
                    <input type="email" v-model="credentials.email" required autocomplete="off" placeholder="Username or Email">
                  </div>
                  <!-- Single Form End -->
                  <!-- Single Form Start -->
                  <div class="single-form password-field">
                    <input
                      :type="showPassword ? 'text' : 'password'"
                      v-model="credentials.password"
                      required
                      autocomplete="off"
                      placeholder="Password"
                    >
                    <button type="button" class="toggle-btn" @click="showPassword = !showPassword">
                      {{ showPassword ? "🔒" : "👁️" }}
                    </button>
                  </div>
                  <div class="single-form">
                    Forgot Password? You can reset - <router-link to='/forgot-password' class='forgot-password-link'>here</router-link>
                  </div>
                  <!-- Single Form End -->
                  <!-- Single Form Start -->
                  <div class="single-form">
                    <button type="submit" class="btn btn-primary btn-hover-dark w-100">Login</button>
                  </div>
                  <!-- Single Form End -->
                </form>
                <p v-if="error" class="error_message">{{error}}</p>
                <div v-if="showResendOption" class="single-form resend-verification">
                  <button type="button" class="btn-link" @click="handleResend" :disabled="resending">
                    {{ resending ? 'Sending...' : 'Resend verification email' }}
                  </button>
                  <p v-if="resendMessage" class="resend-message">{{ resendMessage }}</p>
                </div>
              </div>
            </div>
            <!-- Register & Login Form End -->

          </div>
        </div>
      </div>
      <!-- Register & Login Wrapper End -->

    </div>
  </div>
  <!-- Register & Login End -->

</template>

<script>
import { jwtDecode } from "jwt-decode";
import { loginUser, resendVerificationEmail } from '@/services/userService.js';
import { getUserPackageDetails } from '@/services/subscriptionService';

export default {
  name: 'Login',
  data() {
    return {
      error: "",
      showPassword: false,
      resending: false,
      resendMessage: "",
      credentials: {
        email: '',
        password: ''
      }
    };
  },
  computed: {
    showResendOption() {
      return this.error === 'Please verify your email and then login!';
    }
  },
  methods: {
    async handleResend() {
      this.resending = true;
      this.resendMessage = "";
      try {
        const response = await resendVerificationEmail(this.credentials.email);
        this.resendMessage = response.message || 'Verification email sent. Please check your inbox.';
      } catch (err) {
        this.resendMessage = err.response?.data?.message || 'Failed to resend verification email. Please try again.';
      } finally {
        this.resending = false;
      }
    },
    async login() {
      this.resendMessage = "";
      try {
        const response = await loginUser(this.credentials);

        this.error = "";
        localStorage.setItem('access_token', response.access_token);
        const decoded = jwtDecode(response.access_token);
        const user = decoded;
        
        // Check if there's a redirect after login (from About page or other pages)
        const redirectAfterLogin = localStorage.getItem('redirect_after_login');
        
        if (redirectAfterLogin) {
          // Clear the redirect flag and navigate to the intended page
          localStorage.removeItem('redirect_after_login');
          setTimeout(() => {
            this.$router.push(redirectAfterLogin);
          }, 500);
          return;
        }
        
        // Check if there's a pending package purchase (from Packages page)
        const pendingPackage = localStorage.getItem('pending_package_purchase');
        
        if (pendingPackage) {
          // User was trying to purchase a package
          // First check if they already have a subscription
          try {
            const packageResponse = await getUserPackageDetails(user.id);
            const userPackage = packageResponse.data;
            
            if (userPackage && userPackage.pkg_subscribed) {
              // User already has a subscription - go to dashboard
              console.log('User already has subscription:', userPackage.pkg_subscribed);
              localStorage.removeItem('pending_package_purchase');
              setTimeout(() => {
                this.$router.push('/dashboard/user/home');
              }, 500);
            } else {
              // No subscription - proceed to checkout
              localStorage.removeItem('pending_package_purchase');
              setTimeout(() => {
                this.$router.push(`/checkout/${pendingPackage}`);
              }, 500);
            }
          } catch (err) {
            // Error checking subscription - proceed to checkout anyway
            console.error('Error checking subscription:', err);
            localStorage.removeItem('pending_package_purchase');
            setTimeout(() => {
              this.$router.push(`/checkout/${pendingPackage}`);
            }, 500);
          }
        } else {
          // Normal login flow - redirect based on role
          setTimeout(() => {
            switch (user.role) {
              case 'admin':
                this.$router.push({ path: '/dashboard/admin/home' });
                break;
              case 'manager':
                this.$router.push({ name: 'OrgDashboard' });
                break;
              case 'member':
                this.$router.push({ path: '/dashboard/user/home' });
                break;
              default:
                this.$router.push('/'); // Fallback to home
            }
          }, 500);
        }

      } catch (err) {
        console.error('Login failed:', err);
        // Better error handling
        if (err.code === 'ERR_NETWORK') {
          this.error = 'Cannot connect to server. Please try again later.';
        } else {
          this.error = err.response?.data?.message || 'Login failed. Please try again.';
        }
      }
    }
  }
};
</script>

<style scoped>
.forgot-password-link {
  color: #309255;
  text-decoration: underline dotted #309255;
}

.password-field {
  position: relative;
}

.password-field input {
  width: 100%;
  padding-right: 40px;
}

.toggle-btn {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
}

.resend-verification {
  margin-top: 10px;
}

.btn-link {
  background: none;
  border: none;
  padding: 0;
  color: #309255;
  text-decoration: underline dotted #309255;
  cursor: pointer;
  font-size: 14px;
}

.btn-link:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.resend-message {
  margin-top: 8px;
  font-size: 13px;
  color: #52565b;
}
</style>
